var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import clsx from 'clsx';
import { isString, mergeProps, useClasses, useLayoutProps, useProps, useSkeletonLoader, } from '@hawkins/helpers';
import { HawkinsGlobalClass } from '@hawkins/styles';
import { useMenuTrigger, usePress } from '../hooks';
import { useCanImageLoad } from '../hooks/useCanImageLoad';
import { createInitials } from '../util/createInitials';
import { avatarPartSizes, useAvatarStyles } from './avatar.styles';
import { AvatarTestIds } from './avatar.testids';
var displayName = 'Avatar';
/**
 * An avatar is simply a graphical representation of a person, place or thing.
 */
export var Avatar = React.forwardRef(function (props, ref) {
    var _a, _b, _c, _d, _e, _f, _g;
    // Props
    var _h = useProps(displayName, props), children = _h.children, classesProp = _h.classes, className = _h.className, color = _h.color, _j = _h.component, Component = _j === void 0 ? 'figure' : _j, endAdornment = _h.endAdornment, fallbackName = _h.fallbackName, icon = _h.icon, _k = _h.iconProps, iconProps = _k === void 0 ? {} : _k, defaultTriggerId = _h.id, imageAlt = _h.imageAlt, imageProps = _h.imageProps, imageSrc = _h.imageSrc, layout = _h.layout, loaderProps = _h.loaderProps, loading = _h.loading, name = _h.name, onClick = _h.onClick, postfixLabel = _h.postfixLabel, prefixLabel = _h.prefixLabel, _l = _h.size, size = _l === void 0 ? 'regular' : _l, styleProp = _h.style, _m = _h.variant, variant = _m === void 0 ? 'avatar-and-name' : _m, restProps = __rest(_h, ["children", "classes", "className", "color", "component", "endAdornment", "fallbackName", "icon", "iconProps", "id", "imageAlt", "imageProps", "imageSrc", "layout", "loaderProps", "loading", "name", "onClick", "postfixLabel", "prefixLabel", "size", "style", "variant"]);
    var hasClickHandler = !!onClick;
    var _o = useMenuTrigger({ defaultTriggerId: defaultTriggerId, triggerIdNamespace: displayName }), CaretIcon = _o.CaretIcon, triggerAriaAttributes = _o.triggerAriaAttributes, id = _o.triggerId;
    var pressProps = usePress({ onPress: onClick }).pressProps;
    // State
    // Check to see if the image can load before rendering it
    var imageStatus = useCanImageLoad(imageSrc);
    // Styles
    var baseClasses = useAvatarStyles();
    var classes = useClasses(displayName, baseClasses, classesProp);
    var style = useLayoutProps({ layout: layout, style: styleProp });
    var circleStyle = useLayoutProps({ layout: { backgroundColor: color } });
    var rootClassName = clsx(classes.HAWKINS_BASE, classes.root, props.className, (_a = {},
        _a[HawkinsGlobalClass.FOCUSABLE] = hasClickHandler,
        _a[classes.rootClickable] = hasClickHandler,
        _a));
    var circleClassName = clsx(classes.circle, (_b = {},
        _b[classes.chip] = variant === 'chip',
        _b[classes.compactChip] = variant === 'chip' && size === 'compact',
        _b[classes.default] = !imageSrc || imageStatus === false || imageStatus === 'error',
        _b[classes.compact] = variant !== 'chip' && size === 'compact',
        _b[classes.small] = variant !== 'chip' && size === 'small',
        _b[classes.regular] = variant !== 'chip' && size === 'regular',
        _b[classes.medium] = variant !== 'chip' && size === 'medium',
        _b[classes.large] = variant !== 'chip' && size === 'large',
        _b));
    var imageClassName = clsx(classes.image, (_c = {},
        _c[classes.chip] = variant === 'chip',
        _c[classes.compact] = variant !== 'chip' && size === 'compact',
        _c[classes.small] = variant !== 'chip' && size === 'small',
        _c[classes.regular] = variant !== 'chip' && size === 'regular',
        _c[classes.medium] = variant !== 'chip' && size === 'medium',
        _c[classes.large] = variant !== 'chip' && size === 'large',
        _c));
    var textContainerClassName = clsx(classes.textContainer, (_d = {},
        _d[classes.compactTextContainer] = variant !== 'chip' && size === 'compact',
        _d[classes.smallTextContainer] = variant !== 'chip' && size === 'small',
        _d[classes.regularTextContainer] = variant !== 'chip' && size === 'regular',
        _d[classes.mediumTextContainer] = variant !== 'chip' && size === 'medium',
        _d[classes.largeTextContainer] = variant !== 'chip' && size === 'large',
        _d));
    var nameClassName = clsx(classes.name, HawkinsGlobalClass.TRUNCATE, (_e = {},
        _e[classes.compactName] = variant !== 'chip' && size === 'compact',
        _e[classes.smallName] = variant !== 'chip' && size === 'small',
        _e[classes.regularName] = variant !== 'chip' && size === 'regular',
        _e[classes.mediumName] = variant !== 'chip' && size === 'medium',
        _e[classes.largeName] = variant !== 'chip' && size === 'large',
        _e));
    var prefixLabelClassName = clsx(classes.label, HawkinsGlobalClass.TRUNCATE, (_f = {},
        _f[classes.compactPrefixLabel] = variant !== 'chip' && size === 'compact',
        _f[classes.smallPrefixLabel] = variant !== 'chip' && size === 'small',
        _f[classes.regularPrefixLabel] = variant !== 'chip' && size === 'regular',
        _f[classes.mediumPrefixLabel] = variant !== 'chip' && size === 'medium',
        _f[classes.largePrefixLabel] = variant !== 'chip' && size === 'large',
        _f));
    var postfixLabelClassName = clsx(classes.label, HawkinsGlobalClass.TRUNCATE, (_g = {},
        _g[classes.compactPostfixLabel] = variant !== 'chip' && size === 'compact',
        _g[classes.smallPostfixLabel] = variant !== 'chip' && size === 'small',
        _g[classes.regularPostfixLabel] = variant !== 'chip' && size === 'regular',
        _g[classes.mediumPostfixLabel] = variant !== 'chip' && size === 'medium',
        _g[classes.largePostfixLabel] = variant !== 'chip' && size === 'large',
        _g));
    // Content
    var circleSkeletonLoaderNode = useSkeletonLoader({
        loaderProps: __assign({ variant: 'circle', className: circleClassName }, loaderProps),
        loading: loading,
        style: style,
    });
    var textSkeletonLoaderNode = useSkeletonLoader({
        loaderProps: __assign({ height: avatarPartSizes[size].textLoaderHeight, variant: 'square', width: 75 }, loaderProps),
        loading: loading,
    });
    if (circleSkeletonLoaderNode || textSkeletonLoaderNode) {
        return (_jsxs(Component, __assign({ className: rootClassName }, { children: [circleSkeletonLoaderNode, variant === 'avatar-and-name' && textSkeletonLoaderNode && (_jsx("div", __assign({ className: textContainerClassName }, { children: textSkeletonLoaderNode })))] })));
    }
    // Circle
    var circleContents = createInitials(fallbackName || name);
    if (icon) {
        circleContents = React.createElement(icon, iconProps);
    }
    else if (imageStatus === 'loaded' && imageSrc) {
        circleContents = (_jsx("img", __assign({ className: imageClassName, src: imageSrc, alt: imageAlt || (isString(name) ? name : ''), "data-testid": AvatarTestIds.IMAGE }, imageProps)));
    }
    return (_jsxs(Component, __assign({ className: rootClassName, "data-testid": AvatarTestIds.ROOT, id: id, ref: ref, style: style, tabIndex: hasClickHandler ? 0 : undefined }, triggerAriaAttributes, mergeProps(restProps, pressProps), { children: [_jsx("span", __assign({ "data-testid": AvatarTestIds.AVATAR, style: circleStyle, className: circleClassName }, { children: circleContents })), variant === 'avatar-and-name' && (_jsxs("div", __assign({ className: textContainerClassName }, { children: [_jsx("div", __assign({ className: prefixLabelClassName }, { children: prefixLabel })), _jsx("div", __assign({ className: nameClassName }, { children: children || name })), _jsx("div", __assign({ className: postfixLabelClassName }, { children: postfixLabel }))] }))), !!endAdornment && (_jsx("span", __assign({ "data-testid": AvatarTestIds.END_ADORNMENT, className: classes.endAdornment }, { children: endAdornment }))), CaretIcon && _jsx(CaretIcon, { "aria-hidden": true, className: classes.caret, size: "small" })] })));
});
Avatar.displayName = displayName;
